/**
 * モデル選択ポータルドロップダウンの共通プレゼンテーション部品。
 *
 * シナリオ編集ページの「動画を生成」ドロップダウン (VideoModelDropdownPortal) の
 * 見た目を正本とし、シナリオ設定画面の「シナリオ生成AIモデル」ドロップダウン
 * (ScenarioModelDropdownPortal) と共通化する。スタイルは従来どおり
 * ScenarioGenerator.module.css の modelDropdown* クラスを単一の定義源とする。
 *
 * 構成: Shell (ポータル + フィルタチップ行) / SectionHeader (アイコン + グループ名) /
 * Separator / Item (ラベル + pt バッジ)。ロジック (何をどう絞るか) は各呼び出し側が持つ。
 */
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from '../scenario/ScenarioGenerator.module.css';
import { Tooltip } from '../../common/Tooltip';

export interface ModelDropdownFilter {
  label: string;
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
}

export function ModelDropdownMenuShell({
  menuRef,
  pos,
  filter,
  emptyText,
  standardSurface = false,
  children,
}: {
  menuRef: React.RefObject<HTMLDivElement | null>;
  pos: { top: number; left: number };
  filter?: ModelDropdownFilter;
  emptyText: string;
  /** 共通の枠線なしサーフェスを使う（既存画面は既定の見た目を維持）。 */
  standardSurface?: boolean;
  children: React.ReactNode[];
}) {
  return createPortal(
    <div
      ref={menuRef}
      className={standardSurface ? `modal-surface ${styles.modelDropdownMenuStandard}` : styles.modelDropdownMenu}
      style={{ position: 'fixed', top: pos.top, left: pos.left }}
    >
      {filter && filter.options.length > 1 && (
        <div className={styles.modelDropdownFilterRow}>
          <span className={styles.modelDropdownFilterLabel}>{filter.label}</span>
          <div className={styles.modelDropdownFilterChips}>
            {filter.options.map((o) => (
              <button
                key={o.value}
                type="button"
                className={`${styles.modelDropdownFilterChip} ${filter.value === o.value ? styles.modelDropdownFilterChipActive : ''}`}
                onClick={() => filter.onChange(o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {children.length > 0 ? children : (
        <div className={styles.modelDropdownEmpty}>{emptyText}</div>
      )}
    </div>,
    document.body,
  );
}

/**
 * グループ見出し。iconMask は currentColor で塗る白系 SVG のパス、icon は絵文字等をそのまま置く
 * (Gemini 画像モデルの 🍌 など。ImageModelFamilySelector の見出しと揃える)。
 * tooltip を渡すとラベル部分をカスタムツールチップで包む
 * (画像モデルの通称「標準 / 長時間」に正式名称「GPT Image 2.5 Flare」等を添える用途)。
 * sub=true は見出しの 1 段下 (セクション内のファミリー名) 用で、少し字下げして大文字化しない。
 */
export function ModelDropdownSectionHeader({
  label,
  iconMask,
  icon,
  tooltip,
  sub = false,
}: {
  label: string;
  iconMask?: string;
  icon?: ReactNode;
  tooltip?: string | null;
  sub?: boolean;
}) {
  return (
    <div
      className={`${styles.modelDropdownSectionHeader} ${sub ? styles.modelDropdownSubHeader : ''}`}
      style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
    >
      {icon && (
        <span aria-hidden="true" style={{ fontSize: '11px', lineHeight: 1 }}>{icon}</span>
      )}
      {iconMask && (
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: '11px',
            height: '11px',
            backgroundColor: 'currentColor',
            WebkitMaskImage: `url("${iconMask}")`,
            WebkitMaskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskImage: `url("${iconMask}")`,
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
          }}
        />
      )}
      {tooltip ? (
        <Tooltip content={tooltip} placement="bottom">
          <span>{label}</span>
        </Tooltip>
      ) : (
        label
      )}
    </div>
  );
}

export function ModelDropdownSeparator() {
  return <div className={styles.modelDropdownSeparator} />;
}

export function ModelDropdownItem({
  label,
  pts,
  active,
  disabled,
  onSelect,
}: {
  label: React.ReactNode;
  /** 右端の pt バッジ表記 ("30pt" / "2~30pt" 等)。undefined なら非表示 */
  pts?: string;
  active?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.modelDropdownItem} ${active ? styles.modelDropdownItemActive : ''}`}
      style={disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
      onClick={() => {
        if (disabled) return;
        onSelect();
      }}
    >
      <span className={styles.modelDropdownItemLabel}>{label}</span>
      {pts !== undefined && <span className={styles.modelDropdownItemPts}>{pts}</span>}
    </button>
  );
}
